import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function Card({ title, subtitle, icon, action, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const showBody = !collapsible || open;
  const body = (
    <>
      {subtitle && <div style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>{subtitle}</div>}
      {children}
    </>
  );
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E5E5E5',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16,
      }}
    >
      {(title || action || collapsible) && (
        <div
          onClick={collapsible ? () => setOpen((o) => !o) : undefined}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: showBody ? (subtitle ? 4 : 14) : 0, flexWrap: 'wrap', gap: 8,
            cursor: collapsible ? 'pointer' : 'default',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 500 }}>
            {icon}
            {title}
            {collapsible && (
              <span style={{ fontSize: 12, color: '#999' }}>
                {open ? '^' : 'v'}
              </span>
            )}
          </div>
          {action && <span onClick={(e) => e.stopPropagation()}>{action}</span>}
        </div>
      )}
      {collapsible ? (
        <AnimatePresence initial={false}>
          {showBody && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              {body}
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        showBody && body
      )}
    </div>
  );
}

// Shared animated modal shell — handles the backdrop fade and panel
// scale/fade so individual modals only need to supply their content.
// Callers are responsible for wrapping their conditional render in
// <AnimatePresence> so the exit animation gets a chance to play.
export function Modal({ onClose, panelStyle, children }) {
  return (
    <motion.div
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
        style={{ background: '#fff', borderRadius: 12, width: 460, maxWidth: '90vw', boxShadow: '0 16px 40px rgba(0,0,0,0.18)', ...panelStyle }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export { AnimatePresence };

export function Button({ children, onClick, variant = 'default', size = 'md', disabled = false, type = 'button' }) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 8,
    border: '1px solid #D5D5D5',
    background: '#fff',
    color: '#1A1A1A',
    fontSize: size === 'sm' ? 12 : 13,
    padding: size === 'sm' ? '5px 10px' : '7px 14px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontFamily: 'inherit',
  };
  const variantStyle =
    variant === 'primary'
      ? { background: '#3DC372', borderColor: '#3DC372', color: '#fff' }
      : variant === 'danger'
        ? { color: '#A32D2D', borderColor: '#F3C9C9' }
        : {};
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...base, ...variantStyle }}>
      {children}
    </button>
  );
}

// Mute is stored in localStorage per banner `id` (not per-session), so it
// survives refresh but is scoped to this browser — matching the "pop up
// again each login" behavior the org wanted, once a mute duration elapses.
const MUTE_OPTIONS = [
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
];

function mutedUntil(id) {
  if (!id) return 0;
  return Number(localStorage.getItem(`banner-mute-${id}`)) || 0;
}

export function OutstandingBanner({ id, title, items = [], onAction, actionLabel }) {
  const [dismissed, setDismissed] = useState(false);
  const [muted, setMuted] = useState(() => mutedUntil(id) > Date.now());
  const [muteMenuOpen, setMuteMenuOpen] = useState(false);

  const mute = (ms) => {
    if (id) localStorage.setItem(`banner-mute-${id}`, String(Date.now() + ms));
    setMuteMenuOpen(false);
    setMuted(true);
  };

  return (
    <AnimatePresence>
      {!dismissed && !muted && (
        <motion.div
          key="outstanding-banner"
          initial={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <div style={{ background: '#FCEBEB', borderRadius: 12, padding: '14px 18px', marginBottom: 18, position: 'relative' }}>
            <div style={{ position: 'absolute', top: 12, right: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMuteMenuOpen((o) => !o)}
                  aria-label="Mute notification"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A32D2D', fontSize: 14 }}
                >
                  🔕
                </button>
                {muteMenuOpen && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 2, background: '#fff', border: '1px solid #D5D5D5', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10, whiteSpace: 'nowrap' }}>
                    {MUTE_OPTIONS.map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => mute(opt.ms)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        Mute for {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setDismissed(true)}
                aria-label="Dismiss notification"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A32D2D', fontSize: 16 }}
              >
                ✕
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: '#A32D2D', marginBottom: 8, paddingRight: 48 }}>
              ⚠ {title}
            </div>
            {items.map((item, i) =>
              item.header ? (
                <div key={i} style={{ fontSize: 11, fontWeight: item.fontWeight || 600, color: '#A32D2D', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 0 3px', opacity: 0.8 }}>
                  {item.label}
                </div>
              ) : (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: '#791F1F' }}>
                  <span>{item.label}</span>
                  {item.meta && <span style={{ fontWeight: 500 }}>{item.meta}</span>}
                </div>
              )
            )}
            {onAction && (
              <Button size="sm" onClick={onAction}>
                {actionLabel}
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Tab row with a sliding underline that animates between tabs via a shared
// layoutId — Framer Motion matches the outgoing and incoming underline
// elements across the mount/unmount and tweens between their positions.
export function TabBar({ tabs, activeTab, onChange, counts = {} }) {
  return (
    <div style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid #E5E5E5' }}>
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          style={{
            position: 'relative', padding: '8px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer',
            color: activeTab === t ? '#ED1C24' : '#666',
            fontWeight: activeTab === t ? 500 : 400, marginBottom: -1,
          }}
        >
          {t}{counts[t] != null ? ` (${counts[t]})` : ''}
          {activeTab === t && (
            <motion.div
              layoutId="tab-underline"
              transition={{ type: 'spring', stiffness: 500, damping: 40 }}
              style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, background: '#ED1C24' }}
            />
          )}
        </button>
      ))}
    </div>
  );
}

// Loading placeholder shaped like the content it stands in for — pass a
// width/height/radius matching the real element so the page doesn't jump
// when data arrives.
export function Skeleton({ width = '100%', height = 14, radius = 6, style }) {
  return (
    <motion.div
      animate={{ opacity: [0.55, 1, 0.55] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      style={{ width, height, borderRadius: radius, background: '#E9E9EA', flexShrink: 0, ...style }}
    />
  );
}

// Centered placeholder for "nothing to show yet" — either a genuinely empty
// list (with a primary action to create the first item) or a filtered list
// with zero matches (with an action to clear those filters).
export function EmptyState({ icon = '🗂️', title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 20px' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{title}</div>
      {description && (
        <div style={{ fontSize: 13, color: '#666', marginBottom: 18, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
          {description}
        </div>
      )}
      {action}
    </div>
  );
}

// Searchable multi-select dropdown for picking people from a roster —
// originally built for the entry-form dancer/choreographer picker, also
// reused by the group editor's dancer selection. `extraOption`
// ({ label, checked, onToggle }), if given, adds one extra checkbox row
// pinned below the roster list (e.g. the choreographer picker's
// "Other (not attending)").
export function Picker({ open, onToggle, value, placeholder, options, selectedIds, onToggleId, onSelectAll, groups, onLoadGroup, extraOption }) {
  const [search, setSearch] = useState('');
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const filtered = useMemo(
    () => options.filter((o) => `${o.firstName} ${o.lastName}`.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selectedIds.includes(o.id));
  const selectedInFiltered = filtered.filter((o) => selectedIds.includes(o.id)).length;
  return (
    <div style={{ border: '1px solid #D5D5D5', borderRadius: 8, background: '#fff' }}>
      <div onClick={onToggle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', minHeight: 38, gap: 8 }}>
        <span style={{ fontSize: 14, color: value ? '#1A1A1A' : '#999', flex: 1 }}>{value || placeholder}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.15 }}>▾</motion.span>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="picker-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ borderTop: '1px solid #E5E5E5' }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #E5E5E5' }}>
                <input style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13 }} placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #E5E5E5', position: 'relative' }}>
                <span style={{ fontSize: 12, color: '#666' }}>{selectedInFiltered} of {filtered.length} selected</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {groups && groups.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setGroupMenuOpen((o) => !o)}
                      style={{ fontSize: 12, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      Select group
                    </button>
                  )}
                  {onSelectAll && filtered.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onSelectAll(filtered.map((o) => o.id), allFilteredSelected)}
                      style={{ fontSize: 12, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {allFilteredSelected ? 'Clear all' : 'Select all'}
                    </button>
                  )}
                </div>
                {groupMenuOpen && (
                  <div style={{ position: 'absolute', top: '100%', right: 12, marginTop: 2, background: '#fff', border: '1px solid #D5D5D5', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10, minWidth: 160 }}>
                    {groups.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => { onLoadGroup(g.id); setGroupMenuOpen(false); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#2D2E33', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        👥 {g.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                {filtered.map((o) => (
                  <div key={o.id} onClick={() => onToggleId(o.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #E5E5E5' }}>
                    <input type="checkbox" checked={selectedIds.includes(o.id)} readOnly style={{ accentColor: '#ED1C24', width: 16, height: 16 }} />
                    <span style={{ fontSize: 14 }}>{o.firstName} {o.lastName}</span>
                    <span style={{ fontSize: 12, color: '#666', marginLeft: 'auto' }}>Gr. {o.grade}</span>
                  </div>
                ))}
              </div>
              {extraOption && (
                <div onClick={extraOption.onToggle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: '#FAFAFA' }}>
                  <input type="checkbox" checked={extraOption.checked} readOnly style={{ accentColor: '#ED1C24', width: 16, height: 16 }} />
                  <span style={{ fontSize: 14, fontStyle: 'italic' }}>{extraOption.label}</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
